import { useRef, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import type { GridColDef } from "@mui/x-data-grid-pro"
import { skipToken } from "@reduxjs/toolkit/query"
import { DataGrid } from "@core/components/dataGrid/dataGrid"
import type { MessageContent } from "@core/components/telegram/types"
import type { ADV } from "@/modules/adv2/interfaces/adv"
import {
  useGetOrderQuery,
  useOrderFiltersListQuery,
  useOrdersListQuery,
  useTypesListQuery,
} from "@/modules/adv2/store/advApi"
import type {
  Bindings,
  NamedId,
  ReadBindings,
} from "../../interface/publications"
import {
  bindingUrl,
  getActiveBindingSlots,
  getMissingActiveBindings,
} from "../../utils/bindings"
import { isExternalId } from "../../utils/ids"
import {
  buildOrderAssignments,
  buildOrderListRequest,
  orderFilterKeys,
  orderLinkProposals,
  type OrderLinkAssignment,
  type OrderLinkProposal,
} from "../../services/adv/orderLinks"
import { CatalogSelect } from "./catalogSelect"
import { RefreshTableButton } from "../common/refreshTableButton"
import { bindingCatalogs } from "../../services/adv/catalogs"

export interface OrderLinkPickerProps {
  open: boolean
  onClose: () => void
  content: MessageContent
  /** Always pass current draft bindings, including inactive and legacy values. */
  bindings: ReadBindings
  /** Only the explicitly assigned replacements; merge into the local draft. */
  onApply: (replacements: Bindings) => void
  onCreateVersion?: () => void
  disabled?: boolean
}

const filterLabels = {
  agent_types: "Тип контрагента",
  agents: "Контрагент",
  campaigns: "Кампания",
}

export function OrderLinkPicker(props: OrderLinkPickerProps) {
  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          m: { xs: 1, sm: 3 },
          width: { xs: "calc(100% - 16px)", sm: "calc(100% - 48px)" },
          maxHeight: "calc(100dvh - 32px)",
        },
      }}
    >
      <DialogTitle>Ссылки из ордера ADV</DialogTitle>
      {props.open && <OrderLinkPickerContent {...props} />}
      <DialogActions>
        <Button onClick={props.onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  )
}

function OrderLinkPickerContent({
  content,
  bindings,
  onApply,
  onClose,
  disabled,
  onCreateVersion,
}: OrderLinkPickerProps) {
  // Intentionally independent of location, global ADV forms and PostBot list state.
  const [filters, setFilters] = useState<
    Partial<Record<(typeof orderFilterKeys)[number], NamedId>>
  >({})
  const [pagination, setPagination] = useState({ page: 0, pageSize: 50 })
  const [selectedOrder, setSelectedOrder] = useState<number | null>(null)
  const [showDetails, setShowDetails] = useState(false)
  const narrow = useMediaQuery(useTheme().breakpoints.down("md"))
  const hasSlots = getActiveBindingSlots(content).length > 0
  const catalog = useOrderFiltersListQuery({})
  const types = useTypesListQuery({ queryParams: { table: "bots" } })
  const request = buildOrderListRequest(
    Object.fromEntries(
      orderFilterKeys.map((key) => [
        key,
        filters[key] ? [filters[key]!.id] : [],
      ]),
    ),
    pagination,
  )
  const orders = useOrdersListQuery(request, {
    refetchOnMountOrArgChange: true,
  })
  const detail = useGetOrderQuery(
    selectedOrder === null ? skipToken : { pathParams: { id: selectedOrder } },
    { refetchOnMountOrArgChange: true },
  )
  const page =
    !orders.isFetching && !orders.error ? orders.currentData : undefined
  const count = useRef(0)
  if (page) count.current = page.count
  // Never use RTK's `data`: it can belong to the previously selected argument.
  const currentOrder =
    selectedOrder !== null &&
    !orders.isFetching &&
    !orders.error &&
    !detail.isFetching &&
    !detail.error &&
    detail.currentData?.id === selectedOrder
      ? detail.currentData
      : undefined
  const proposals = currentOrder
    ? orderLinkProposals(
        currentOrder,
        bindingCatalogs(
          catalog.error ? undefined : catalog.currentData,
          types.error ? undefined : types.currentData,
        ),
      )
    : undefined
  const columns = (
    [
      {
        field: "id",
        headerName: "Ордер",
        width: 190,
        renderCell: ({ row }) => (
          <Button
            disabled={disabled}
            aria-pressed={selectedOrder === row.id}
            onClick={() => {
              setSelectedOrder(row.id)
              setShowDetails(true)
            }}
          >
            Выбрать ордер {row.id}
          </Button>
        ),
      },
      {
        field: "campaign_name",
        headerName: "Кампания",
        flex: 1,
        minWidth: 160,
      },
      {
        field: "agent",
        headerName: "Контрагент",
        flex: 1,
        minWidth: 160,
        valueGetter: ({ row }) => row.agent?.name,
      },
    ] satisfies GridColDef<ADV.Order>[]
  ).map((column) => ({ ...column, sortable: false, filterable: false }))
  return (
    <DialogContent sx={{ px: { xs: 2, sm: 3 }, minHeight: 0 }}>
      {!hasSlots ? (
        <Stack spacing={2}>
          <Alert severity="info">В этой версии нет мест для подстановки</Alert>
          {onCreateVersion && (
            <Button disabled={disabled} onClick={onCreateVersion}>
              Редактирование
            </Button>
          )}
        </Stack>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: narrow
              ? "minmax(0, 1fr)"
              : "minmax(0, 1fr) minmax(0, 1fr)",
            gap: 2,
            height: "min(680px, calc(100dvh - 180px))",
            minHeight: 0,
          }}
        >
          <Box
            component="section"
            aria-label="Список ордеров"
            hidden={narrow && showDetails}
            sx={{ minWidth: 0, minHeight: 0, overflow: "auto" }}
          >
            <Stack spacing={1} sx={{ height: "100%", minHeight: 0 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    sm: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 1,
                }}
              >
                {orderFilterKeys.map((key) => (
                  <CatalogSelect
                    key={key}
                    label={filterLabels[key]}
                    value={filters[key] ?? null}
                    options={
                      catalog.error
                        ? []
                        : (catalog.currentData?.[key] ?? []).filter(({ id }) =>
                            isExternalId(id),
                          )
                    }
                    onChange={(value) => {
                      setSelectedOrder(null)
                      setFilters((current) => ({
                        ...current,
                        [key]: value ?? undefined,
                      }))
                      setPagination((current) => ({ ...current, page: 0 }))
                    }}
                    isFetching={catalog.isFetching}
                    disabled={disabled}
                  />
                ))}
              </Box>
              {catalog.error != null && (
                <Alert severity="warning">
                  Не удалось загрузить фильтры ADV.{" "}
                  <Button onClick={() => void catalog.refetch()}>
                    Повторить загрузку фильтров
                  </Button>
                </Alert>
              )}
              {orders.error != null && (
                <Alert severity="warning">
                  Не удалось загрузить ордера. Сохранённые значения не изменены.
                </Alert>
              )}
              <RefreshTableButton
                label="Обновить ордера"
                disabled={orders.isFetching || disabled}
                onClick={() => {
                  setSelectedOrder(null)
                  void orders.refetch()
                }}
              />
              <Box sx={{ width: "100%", minHeight: 180, flex: 1 }}>
                <DataGrid
                  sx={{ "&&": { height: "100%" } }}
                  rows={page?.data ?? []}
                  columns={columns}
                  pagination
                  paginationMode="server"
                  paginationModel={pagination}
                  onPaginationModelChange={(next) => {
                    if (
                      next.page !== pagination.page ||
                      next.pageSize !== pagination.pageSize
                    ) {
                      setSelectedOrder(null)
                      setPagination(next)
                    }
                  }}
                  pageSizeOptions={[25, 50, 100]}
                  rowCount={count.current}
                  loading={orders.isFetching}
                  disableColumnFilter
                  disableColumnMenu
                  disableRowSelectionOnClick
                />
              </Box>
            </Stack>
          </Box>
          <Box
            component="section"
            aria-label="Ссылки ордера"
            hidden={narrow && !showDetails}
            sx={{ minWidth: 0, minHeight: 0, overflow: "auto" }}
          >
            <Stack spacing={2}>
              {narrow && (
                <Button
                  sx={{ alignSelf: "flex-start" }}
                  onClick={() => setShowDetails(false)}
                >
                  К списку ордеров
                </Button>
              )}
              <Typography variant="subtitle1">
                {selectedOrder === null
                  ? "Выберите ордер"
                  : `Ссылки ордера ${selectedOrder}`}
              </Typography>
              {selectedOrder !== null && detail.isFetching && (
                <Typography role="status">Загрузка ссылок ордера…</Typography>
              )}
              {selectedOrder !== null && detail.error != null && (
                <Alert severity="warning">
                  Не удалось загрузить ссылки.{" "}
                  <Button onClick={() => void detail.refetch()}>
                    Повторить загрузку ссылок
                  </Button>
                </Alert>
              )}
              {proposals && (
                <Assignments
                  key={JSON.stringify([selectedOrder, proposals])}
                  proposals={proposals}
                  content={content}
                  bindings={bindings}
                  disabled={disabled}
                  onApply={(replacements) => {
                    onApply(replacements)
                    onClose()
                  }}
                />
              )}
            </Stack>
          </Box>
        </Box>
      )}
    </DialogContent>
  )
}

function Assignments({
  proposals,
  content,
  bindings,
  onApply,
  disabled,
}: Pick<
  OrderLinkPickerProps,
  "content" | "bindings" | "onApply" | "disabled"
> & { proposals: OrderLinkProposal[] }) {
  const targets = getActiveBindingSlots(content)
  const [assignments, setAssignments] = useState<OrderLinkAssignment[]>([])
  const [error, setError] = useState<string>()
  const validate = () =>
    buildOrderAssignments(content, bindings, proposals, assignments)
  const attempt = () => {
    if (disabled) return
    try {
      const replacements = validate()
      setError(undefined)
      onApply(replacements)
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : "Назначения недоступны",
      )
    }
  }
  return (
    <Stack spacing={2}>
      {targets.length > 0 &&
        !getMissingActiveBindings(content, bindings).length && (
          <Alert severity="info">Все ссылки заполнены</Alert>
        )}
      {!proposals.length && <Typography>В ордере нет ссылок</Typography>}
      {proposals.map((proposal) => (
        <Box
          key={proposal.id}
          sx={{
            overflowWrap: "anywhere",
            border: 1,
            borderColor: (theme) => alpha(theme.palette.text.primary, 0.14),
            borderRadius: 1,
            p: 1,
          }}
        >
          <Typography>
            {proposal.label}: {proposal.url}
          </Typography>
          {proposal.value?.botType && (
            <Typography>Тип: {proposal.value.botType.name}</Typography>
          )}
          {proposal.reason && (
            <Alert severity="warning">{proposal.reason}</Alert>
          )}
        </Box>
      ))}
      <Typography variant="body2" color="text.secondary">
        Выберите места для ссылок. Значения изменятся только после применения.
      </Typography>
      {targets.map((target) => {
        const assignment = assignments.find(
          ({ slotId }) => slotId === target.slotId,
        )
        const proposal = proposals.find(
          ({ id }) => id === assignment?.proposalId,
        )
        const currentValue =
          bindingUrl(bindings[target.slotId]) ??
          (bindings[target.slotId]?.botId !== undefined
            ? `Сохранённый botId=${bindings[target.slotId].botId}`
            : "Не заполнено")
        return (
          <Stack key={target.slotId} spacing={0.5} sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {target.occurrences
                .map((occurrence) => {
                  const parts = occurrence.path.split("/")
                  if (occurrence.document === "entity-url")
                    return `Текст, ссылка ${Number(parts[3]) + 1}`
                  if (occurrence.document.startsWith("button"))
                    return `Кнопка ${Number(parts[2]) + 1}.${Number(parts[3]) + 1}, ${occurrence.document === "button-text" ? "текст" : "ссылка"}`
                  return `Текст${occurrence.offset === undefined ? "" : `, позиция ${occurrence.offset + 1}`}`
                })
                .join("; ")}
            </Typography>
            <TextField
              size="small"
              select
              SelectProps={{ native: true }}
              label={`${target.label} — предложение`}
              inputProps={{ "aria-label": `${target.label} — предложение` }}
              value={assignment?.proposalId ?? ""}
              disabled={disabled}
              onChange={(event) => {
                const proposalId = event.target.value
                setError(undefined)
                setAssignments((current) => [
                  ...current.filter(({ slotId }) => slotId !== target.slotId),
                  ...(proposalId
                    ? [{ slotId: target.slotId, proposalId }]
                    : []),
                ])
              }}
            >
              <option value="">Не менять</option>
              {proposals
                .filter(({ value, reason }) => value && !reason)
                .map((proposal) => (
                  <option key={proposal.id} value={proposal.id}>
                    {proposal.label}: {bindingUrl(proposal.value)}
                  </option>
                ))}
            </TextField>
            <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
              {proposal
                ? `Было: ${currentValue} → Станет: ${bindingUrl(proposal.value)}`
                : `Сейчас: ${currentValue}`}
            </Typography>
          </Stack>
        )
      })}
      {error && <Alert severity="error">{error}</Alert>}
      {assignments.length > 0 && (
        <Button
          disabled={disabled}
          onClick={() => {
            setAssignments([])
            setError(undefined)
          }}
        >
          Сбросить назначения
        </Button>
      )}
      <Button
        variant="contained"
        onClick={attempt}
        disabled={disabled || !assignments.length}
      >
        Применить в черновик
      </Button>
    </Stack>
  )
}
